import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, UserPlus, Search } from 'lucide-react';

/**
 * PeopleMultiSelect — pick several people, from platform users and/or as
 * free-typed external people.
 *
 * Built for the audit team fields (co-auditors / joint auditors and auditees),
 * where real audits routinely involve external certification-body auditors and
 * contractor reps who have no login on the platform.
 *
 * Value is an array of participant objects matching the backend AuditParticipant
 * shape, so it round-trips straight to the API:
 *   { id, user_id, name, email, organization, role_label, is_external }
 * user_id is null for external people.
 *
 * Props:
 *  - value            participant object array (required)
 *  - onChange(next)   called with the next array
 *  - options          [{ id, name, role_label, department, email }] selectable users
 *  - label            field label
 *  - placeholder      search input placeholder
 *  - allowExternal    show the "add external person" row (default false)
 *  - excludeIds       user ids to hide from options (e.g. the lead auditor)
 *  - maxItems         cap on selections (default 50, matching backend MAX_PARTICIPANTS)
 *  - disabled         render read-only
 *  - helpText         small hint under the label
 */
const PeopleMultiSelect = ({
  value = [],
  onChange,
  options = [],
  label,
  placeholder = 'Search people — pick multiple',
  allowExternal = false,
  excludeIds = [],
  maxItems = 50,
  disabled = false,
  helpText,
}) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [externalName, setExternalName] = useState('');
  const [externalOrg, setExternalOrg] = useState('');
  const dropdownRef = useRef(null);

  // Close the dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const atLimit = selected.length >= maxItems;

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options.filter((opt) => {
      if (excludeIds.includes(opt.id)) return false;
      if (!term) return true;
      return (
        (opt.name || '').toLowerCase().includes(term) ||
        (opt.department || '').toLowerCase().includes(term) ||
        (opt.role_label || '').toLowerCase().includes(term)
      );
    });
  }, [options, search, excludeIds]);

  const isSelected = (userId) => selected.some((s) => s.user_id === userId);

  const toggleUser = (opt) => {
    if (isSelected(opt.id)) {
      onChange(selected.filter((s) => s.user_id !== opt.id));
      return;
    }
    if (atLimit) return;
    onChange([
      ...selected,
      {
        user_id: opt.id,
        name: opt.name || '',
        email: opt.email || null,
        organization: null,
        role_label: opt.role_label || null,
        is_external: false,
      },
    ]);
  };

  const addExternal = () => {
    const name = externalName.trim();
    if (!name || atLimit) return;
    // Don't add the same external twice — backend de-dupes on lowercased name
    const exists = selected.some(
      (s) => !s.user_id && (s.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setExternalName('');
      setExternalOrg('');
      return;
    }
    onChange([
      ...selected,
      {
        user_id: null,
        name,
        email: null,
        organization: externalOrg.trim() || null,
        role_label: null,
        is_external: true,
      },
    ]);
    setExternalName('');
    setExternalOrg('');
  };

  const removeAt = (index) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
          {selected.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
              {selected.length} selected
            </span>
          )}
        </label>
      )}
      {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}

      {/* Search + option dropdown */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => !disabled && setShowDropdown(true)}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
        />
      </div>

      {showDropdown && !disabled && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No matching people
            </div>
          )}
          {filteredOptions.slice(0, 50).map((opt) => {
            const checked = isSelected(opt.id);
            return (
              <label
                key={opt.id}
                className={`w-full flex items-center justify-between gap-2 px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  checked ? 'bg-blue-50' : 'hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUser(opt)}
                    disabled={!checked && atLimit}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 truncate">
                      {opt.name || 'Unknown'}
                    </div>
                    {opt.department && (
                      <div className="text-xs text-gray-500 truncate">{opt.department}</div>
                    )}
                  </div>
                </div>
                {opt.role_label && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded shrink-0">
                    {opt.role_label}
                  </span>
                )}
              </label>
            );
          })}
          {filteredOptions.length > 50 && (
            <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
              Showing first 50 results. Type to refine search.
            </div>
          )}
        </div>
      )}

      {/* Add an external person who has no platform login */}
      {allowExternal && !disabled && (
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <input
            type="text"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExternal();
              }
            }}
            placeholder="External person — full name"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          <input
            type="text"
            value={externalOrg}
            onChange={(e) => setExternalOrg(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExternal();
              }
            }}
            placeholder="Organization (optional)"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
          />
          <button
            type="button"
            onClick={addExternal}
            disabled={!externalName.trim() || atLimit}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" />
            Add
          </button>
        </div>
      )}

      {atLimit && (
        <p className="text-xs text-amber-600 mt-1">
          Maximum of {maxItems} people reached.
        </p>
      )}

      {/* Selected pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map((person, index) => (
            <span
              key={person.id || person.user_id || `${person.name}-${index}`}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                person.is_external || !person.user_id
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {person.name}
              {(person.is_external || !person.user_id) && (
                <span className="text-xs opacity-80">
                  {person.organization ? `(External · ${person.organization})` : '(External)'}
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  className="hover:opacity-60"
                  aria-label={`Remove ${person.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PeopleMultiSelect;
